import React, { useEffect, useRef, useMemo } from 'react';
import { IntroTitle } from 'components/fragment';
import {
  Button,
  ETIDialogFooter,
  ETIDialogBody,
  ETIDialogHeader,
} from '@zawarski/eti-typescript-components';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import backgroundImage from '@/assets/images/img.png';
import {
  CheckboxButton as CheckboxETI,
  SearchableSelect,
} from '@zawarski/eti-typescript-components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { accountSelector } from 'store/account/account.selector';
import displayFullScreen from '@/assets/images/displayFullScreen.png';
import displayColumnsImage from '@/assets/images/displayColumns.png';
import threeVerticalDots from '@/assets/images/threeVerticalDots.png';
import toggleImage from '@/assets/images/toggle.png';
import { SET_PROG_IN_FULLSCREEN } from 'store/app/app.actions';

declare global {
  interface Window {
    dbSettingDone?: boolean;
  }
}

type SetupViewProps = {
  isInitialOnBoarding: boolean;
  stepper: 0 | 1 | 2 | 3;
  programLists: ISelectedPrograms[];
  nextStep: (step: 0 | 1 | 2 | 3) => void;
  onToggleProgram: (id: number | string) => void;
  onClose: () => void;
  updateSettings: (newOnboardingSettings: OnBoarding) => void;
  onBoardingSettings: {
    isInitialized: boolean;
    hasUpdates: boolean;
    hasCheckedHide: boolean;
    programInFullScreen: string;
  };
  setStepState: (step: 0 | 1) => void;
  isMobile?: boolean;
  hasMadeChanges: boolean;
  setHasMadeChanges: (hasMadeChanges: boolean) => void;
  displayMode: string;
  setDisplayMode: (mode: string) => void;
  progInFullScreen: string;
  onCloseResetOnboarding: () => void;
};
const SetupView: React.FC<SetupViewProps> = ({
  isInitialOnBoarding,
  programLists,
  stepper,
  onClose,
  onToggleProgram,
  nextStep,
  updateSettings,
  onBoardingSettings,
  setStepState,
  isMobile,
  hasMadeChanges,
  setHasMadeChanges,
  displayMode,
  setDisplayMode,
  progInFullScreen,
  onCloseResetOnboarding,
}) => {
  const dispatch = useAppDispatch();
  const [dontShowAgain, setDontShowAgain] = React.useState(onBoardingSettings?.hasCheckedHide);
  const account = useAppSelector(accountSelector) || undefined;

  const token = account.accessToken || '';
  const userId = account.id || 0;
  //order programLists alphabetically by label
  const orderedProgramLists = [...programLists]?.sort((a, b) => a.label.localeCompare(b.label));
  //search programLists for active programs and count them
  const activePrograms = programLists?.filter((program) => program.isActive);

  const options = useMemo(() => {
    return orderedProgramLists?.map((program) => ({
      label: program.label,
      value: program.id,
    }));
  }, [orderedProgramLists]);

  const handleOnClose = () => {
    window.dbSettingDone = true; // Set flag to indicate onboarding is done
    //find program id 45 Dot Video WAll, if active open in new window
    const DotVideoWallProgramId = 45;
    const DotVideoWall = programLists?.find((program) => program.id === DotVideoWallProgramId);
    if (DotVideoWall?.isActive) {
      const url = `https://www.palmettoeoc.com/app/dotcameraboard?access_token=${token}&userId=${userId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    if (hasMadeChanges) {
      const progInFullSc =
        displayMode === 'fullscreen'
          ? progInFullScreen && activePrograms?.some((program) => program.id === progInFullScreen)
            ? progInFullScreen
            : activePrograms[0]?.id
          : '';
      const onBoarding = {
        isInitialized: true,
        hasUpdates: false,
        hasCheckedHide: dontShowAgain,
        programInFullScreen: progInFullSc,
      };
      dispatch(SET_PROG_IN_FULLSCREEN(progInFullSc.toString()));
      updateSettings(onBoarding);
    }
    onClose();
    setStepState(0);
    nextStep(0);
  };

  const divARef = useRef<HTMLDivElement>(null);
  const divBRef = useRef<HTMLDivElement>(null);
  const divCRef = useRef<HTMLDivElement>(null);
  const divDRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //execute toggledivB function when displayMode has changed
    const timeout = setTimeout(() => {
      if (displayMode === 'fullscreen') toggleDivB();
      else if (displayMode === 'columns') toggleDivD();
    }, 0);

    return () => clearTimeout(timeout);
  }, [displayMode]);

  const onHandleStep = () => {
    if (stepper === 0) {
      nextStep(1);
    } else if (stepper === 1) {
      nextStep(2);
    } else {
      nextStep(3);
    }
  };

  const onHandleBack = () => {
    if (stepper === 1) {
      nextStep(0);
    } else if (stepper === 2) {
      nextStep(1);
    } else {
      nextStep(2);
    }
  };

  const handleDisplayMode = (mode: string) => {
    if (displayMode !== mode) {
      setHasMadeChanges(true);
      setDisplayMode(mode);
    }
  };

  const CustomButton = ({
    id,
    icon,
    programName,
    isActive,
  }: {
    id: number | string;
    icon: React.ReactNode;
    programName: string;
    isActive: boolean;
  }) => {
    return (
      <div
        className={`flex p-2 gap-2 min-w-[160px] w-full items-center justify-start bg-white-50 rounded-md cursor-pointer ${isActive ? 'text-vulcan-500 border border-vulcan-500' : 'text-lynch-500 border border-lynch-200'}`}
        onClick={() => {
          setHasMadeChanges(true);
          onToggleProgram(id);
        }}>
        {icon}
        <p className='truncate' title={programName}>
          {programName}
        </p>
      </div>
    );
  };

  const DefaultTips = [
    {
      title: 'Tip 1',
      text: 'Make sure you are assigned the incident(s) you are actively working. You can manage incidents in either the popup (seen when onboarding closes) or in the Incidents program located in the side panel.',
    },
    {
      title: 'Tip 2',
      text: 'Full Screen mode allows you see more information in an application by adjusting the number of columns located in the top right',
      image: threeVerticalDots,
    },
    {
      title: 'Tip 3',
      text: 'You can easily switch between full screen tabs mode and viewing multiple programs in columns mode by using the controls in the upper right corner',
      image: toggleImage,
    },
    {
      title: 'Tip 4',
      text: 'In Column view, you can pin a single application program column so it never moves from the far left column',
    },
    {
      title: 'Tip 5',
      text: 'In Full Screen mode, use filters and sorting arrows to arrange the table rows but remember to periodically remove the filters to see all information',
    },
  ];

  const toggleDivB = () => {
    const divB = divBRef.current;
    const divA = divARef.current;

    if (divB && divA) {
      if (divB.classList.contains('opacity-0')) {
        divB.classList.remove('opacity-0', 'max-h-0', 'translate-y-4');
        divB.classList.add('opacity-100', 'max-h-16', 'translate-y-0');

        divA.classList.remove('translate-y-0');
        divA.classList.add('translate-y-[-20px]');
      } else {
        divB.classList.remove('opacity-100', 'max-h-16', 'translate-y-0');
        divB.classList.add('opacity-0', 'max-h-0', 'translate-y-4');

        divA.classList.remove('translate-y-[-20px]');
        divA.classList.add('translate-y-0');
      }
    }
  };

  const toggleDivD = () => {
    const divC = divCRef.current;
    const divD = divDRef.current;

    if (divC && divD) {
      if (divD.classList.contains('opacity-0')) {
        divD.classList.remove('opacity-0', 'max-h-0', 'translate-y-4');
        divD.classList.add('opacity-100', 'max-h-16', 'translate-y-0');

        divC.classList.remove('translate-y-0');
        divC.classList.add('translate-y-[-20px]');
      } else {
        divD.classList.remove('opacity-100', 'max-h-16', 'translate-y-0');
        divD.classList.add('opacity-0', 'max-h-0', 'translate-y-4');

        divC.classList.remove('translate-y-[-20px]');
        divC.classList.add('translate-y-0');
      }
    }
  };

  const TipsCard = ({ title, text, image }: { title: string; text: string; image?: string }) => {
    return (
      <div className={'flexh-full w-full flex-1 p-2.5 bg-white-50 rounded-sm'}>
        <div className={'font-semibold'}>{title}</div>
        <div>{text}</div>
        {image && <img className={'object-contain'} src={image} alt={`${image}`} />}
      </div>
    );
  };

  const Step0View = () => {
    return (
      <div className={`flex flex-col ${isMobile ? 'p-2' : 'p-0 gap-6'}`}>
        <div
          key={'step1header'}
          className={`flex ${isMobile ? 'flex-col' : 'flex-row justify-between items-start gap-6'}`}>
          <IntroTitle
            title='Choose a display mode'
            subtitle='Full Screen Mode will show a tab for each program while Columns Mode can show 6 programs simultaneously and uses a scrollbar to view additional programs.'
          />
          <IconButton
            className={isInitialOnBoarding ? 'hidden' : ''}
            size='large'
            onClick={onCloseResetOnboarding}
            sx={{ padding: 0 }}>
            <CloseIcon fontSize='inherit' />
          </IconButton>
        </div>
        <div className='flex w-full gap-6'>
          <div
            onClick={() => {
              handleDisplayMode('fullscreen');
            }}
            className={`w-1/2 p-4 bg-lynch-100 rounded-sm relative overflow-hidden cursor-pointer ${displayMode === 'fullscreen' ? 'border !border-vulcan-500' : 'border !border-lynch-500'}`}>
            <img
              className={'object-contain'}
              src={displayFullScreen}
              alt={'displayFullScreen'}
              style={{
                WebkitMaskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
                maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              }}
            />
            <div
              ref={divARef}
              id='divA'
              className={`absolute bottom-4 left-4 transition-all duration-1000 text-lg font-bold ${displayMode === 'fullscreen' ? 'text-vulcan-500' : 'text-lynch-500'}`}>
              Full screen
            </div>
            <div
              ref={divBRef}
              id='divB'
              className={`absolute bottom-4 left-4 opacity-0 max-h-0 w-full transform translate-y-4 transition-all duration-1000`}>
              Focus on a single program at once
            </div>
          </div>

          <div
            onClick={() => {
              handleDisplayMode('columns');
            }}
            className={`w-1/2 p-4 bg-lynch-100 rounded-sm relative overflow-hidden cursor-pointer ${displayMode === 'columns' ? 'border !border-vulcan-500' : 'border !border-lynch-500'}`}>
            <img
              className={'object-contain'}
              src={displayColumnsImage}
              alt={'displayColumns'}
              style={{
                WebkitMaskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
                maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
              }}
            />
            <div
              ref={divCRef}
              id='divC'
              className={`absolute bottom-4 left-4 transition-all duration-1000 text-lg font-bold ${displayMode === 'columns' ? 'text-vulcan-500' : 'text-lynch-500'}`}>
              Columns
            </div>
            <div
              ref={divDRef}
              id='divD'
              className={`absolute bottom-4 left-4 opacity-0 max-h-0 w-full transform translate-y-4 transition-all duration-1000`}>
              A better overview for multiple programs
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Step1View = () => {
    return (
      <>
        <ETIDialogHeader
          children={[
            <div
              key={'step1header'}
              className={`flex ${isMobile ? 'flex-col' : 'flex-row justify-between items-start gap-6'}`}>
              <IntroTitle
                title='Choose the programs you will use the most. You can add or remove programs at any time'
                subtitle=''
              />
              <IconButton
                className={isInitialOnBoarding ? 'hidden' : ''}
                size='large'
                onClick={onCloseResetOnboarding}
                sx={{ padding: 0 }}>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </div>,
          ]}
        />
        <ETIDialogBody
          sx={{ padding: 0, overflowX: 'hidden' }}
          children={[
            <div key={'step1body'} className={`flex flex-col ${isMobile ? '' : 'gap-6'}`}>
              <SearchableSelect
                key='searchableSelect'
                options={options || []}
                placeholder={'Search'}
                label=''
                onChange={(_event, option) => {
                  if (Array.isArray(option)) {
                    option = option[0] ?? null;
                  }
                  if (option !== null && typeof option !== 'string' && 'label' in option) {
                    setHasMadeChanges(true);
                    onToggleProgram(Number(option.value));
                  }
                }}
              />
              <div
                className={`grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 self-stretch items-start content-start max-h-[320px]`}>
                {orderedProgramLists.length ? (
                  orderedProgramLists.map((program) => (
                    <CustomButton
                      key={program.id}
                      id={program.id}
                      icon={<SpaceDashboardIcon />}
                      programName={program.label}
                      isActive={program.isActive}
                    />
                  ))
                ) : (
                  <p>No program found. Please contact administrator</p>
                )}
              </div>
            </div>,
          ]}
        />
      </>
    );
  };

  const Step2View = () => {
    return (
      <div className={'flex flex-col p-0 gap-2'}>
        <div
          key={'step1header'}
          className={`flex ${isMobile ? 'flex-col' : 'flex-row justify-between items-start gap-6'}`}>
          <IntroTitle title={'Tips & tricks'} subtitle={''} />
          <IconButton
            className={isInitialOnBoarding ? 'hidden' : ''}
            size='large'
            onClick={onCloseResetOnboarding}
            sx={{ padding: 0 }}>
            <CloseIcon fontSize='inherit' />
          </IconButton>
        </div>
        {DefaultTips.map((tip, index) => (
          <TipsCard key={index} title={tip.title} text={tip.text} image={tip?.image || undefined} />
        ))}
      </div>
    );
  };

  const CompletedView = () => {
    return (
      <div className={'p-0'}>
        <div className={'flex w-full items-center justify-center'}>
          <img className={'w-[271px] object-contain'} src={backgroundImage} alt={'illustration'} />
        </div>
        <IntroTitle title={'Your new dashboard awaits'} subtitle={''} />
        <ETIDialogFooter
          sx={{ padding: 0, width: '100%' }}
          children={[
            <div key={'completedViewFooter'} className='flex flex-col w-full space-y-2'>
              <div className='flex flex-col w-full'>
                <CheckboxETI
                  label={`Don't repeat this setup process each time you open the Dashboard`}
                  checked={dontShowAgain}
                  onChange={() => {
                    setDontShowAgain(!dontShowAgain);
                    setHasMadeChanges(true);
                  }}
                  value={'dontShowAgain'}
                  color={'primary'}
                />
                <div className='w-full flex justify-center mt-4 pb-2'>
                  <Button color={'primary'} onClick={handleOnClose}>
                    Launch Dashboard
                  </Button>
                </div>
              </div>
            </div>,
          ]}
        />
      </div>
    );
  };

  const StepperView = () => {
    switch (stepper) {
      case 0:
        return Step0View();
      case 1:
        return Step1View();
      case 2:
        return Step2View();
      case 3:
        return CompletedView();
      default:
        return null;
    }
  };

  const disableContinueBtn = () => {
    if (stepper === 0 && !displayMode) {
      return true;
    } else if (stepper === 1 && activePrograms && activePrograms.length < 1) {
      return true;
    }
    return false;
  };

  return (
    <div className={`flex flex-col ${isMobile ? 'p-2' : 'p-0 gap-6'}`}>
      {/* Top Right Close Button */}
      {/*<div className='flex justify-end items-center pt-2'>*/}
      {/*  <IconButton size='large' onClick={onCloseResetOnboarding} sx={{ padding: 0 }}>*/}
      {/*    <CloseIcon fontSize='inherit' />*/}
      {/*  </IconButton>*/}
      {/*</div>*/}

      {/* Main Content */}
      <StepperView />

      {/* Footer */}
      {stepper !== 3 && (
        <ETIDialogFooter
          sx={{ padding: 0 }}
          children={[
            <div key={'dialogFooterFinish'} className='flex justify-between w-full p-0'>
              {stepper > 0 ? (
                <Button
                  className='!bg-lynch-100 !text-lynch-900'
                  color='secondary'
                  onClick={onHandleBack}
                  startIcon={<ArrowBackIosIcon />}>
                  GO BACK
                </Button>
              ) : (
                <div></div>
              )}
              <Button color='primary' onClick={onHandleStep} disabled={disableContinueBtn()}>
                {stepper === 0 || (stepper === 1 && activePrograms?.length >= 1)
                  ? 'CONTINUE'
                  : stepper === 1
                    ? `Please select a program`
                    : 'OK, I GET IT NOW'}
              </Button>
            </div>,
          ]}
        />
      )}
    </div>
  );
};

export default SetupView;
